import { defineComponent } from "../lib/component.ts";
import { enableDragScroll } from "../lib/drag-scroll.ts";

const SETTLE_TIMEOUT = 150;
const CLONE_COUNT = 3;
const POINTER_UP_SETTLE_TIMEOUT = 500;
const MAX_VISIBLE_COUNT = 3;

interface RelatedProductsRefs {
  track?: HTMLElement;

  slidesContainer?: HTMLElement;

  slides?: HTMLElement[];
}

class RelatedProducts extends defineComponent<RelatedProductsRefs>()(["track"]) {
  #repositioning = false;

  #settleTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  #mediaQuery = window.matchMedia("(min-width: 768px)");

  #pointerUpTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  #autoplayEnabled = false;

  #autoplayIntervalMs = 5000;

  #autoplayTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  #hoverPaused = false;

  #dragPaused = false;

  #focusPaused = false;

  #inViewport = false;

  #documentVisible = true;

  #intersectionObserver: IntersectionObserver | undefined = undefined;

  connectedCallback(): void {
    super.connectedCallback();

    if (window.Shopify?.designMode) return;

    const slides = this.refs.slides;
    if (!Array.isArray(slides) || slides.length < 2) return;

    this.#buildCarousel(slides);

    this.tabIndex = 0;

    if ("onscrollend" in window) {
      this.refs.track.addEventListener("scrollend", this.#onScrollSettle, {
        signal: this.signal,
      });
    } else {
      this.refs.track.addEventListener("scroll", this.#onScrollFallback, {
        passive: true,
        signal: this.signal,
      });
    }

    this.addEventListener("keydown", this.#onKeyDown, {
      signal: this.signal,
    });
    this.#mediaQuery.addEventListener("change", this.#onMediaChange, {
      signal: this.signal,
    });

    enableDragScroll(this.refs.track, this.signal, { itemSelector: ":scope > *" });

    this.refs.track.addEventListener("pointerdown", this.#onPointerDown, { signal: this.signal });
    this.refs.track.addEventListener("pointerup", this.#onPointerUp, { signal: this.signal });
    this.refs.track.addEventListener("pointercancel", this.#onPointerUp, { signal: this.signal });

    this.#setupAutoplay();
  }

  #buildCarousel(slides: HTMLElement[]): void {
    const track = this.refs.track;
    const count = slides.length;
    const hideClones = count > MAX_VISIBLE_COUNT;

    for (let i = 0; i < CLONE_COUNT; i++) {
      const idx = (((count - CLONE_COUNT + i) % count) + count) % count;
      track.appendChild(this.#makeClone(slides[idx], idx, hideClones));
    }

    for (let i = 0; i < count; i++) {
      slides[i].dataset.index = String(i);
      track.appendChild(slides[i]);
    }

    for (let i = 0; i < CLONE_COUNT; i++) {
      const idx = i % count;
      track.appendChild(this.#makeClone(slides[idx], idx, hideClones));
    }

    const firstReal = track.children[CLONE_COUNT];
    if (firstReal instanceof HTMLElement) {
      track.scrollLeft = firstReal.offsetLeft;
    }
  }

  #makeClone(slide: HTMLElement, idx: number, hide: boolean): HTMLElement {
    const cloned = slide.cloneNode(true);
    if (!(cloned instanceof HTMLElement)) {
      throw new Error("Slide clone is not an HTMLElement");
    }
    cloned.removeAttribute("ref");
    cloned.dataset.clone = "";
    cloned.dataset.index = String(idx);
    if (hide) {
      cloned.setAttribute("aria-hidden", "true");
      const focusable = cloned.querySelectorAll("a, button, [tabindex]");
      for (const el of focusable) {
        el.setAttribute("tabindex", "-1");
      }
    }
    return cloned;
  }

  #onScrollSettle = (): void => {
    this.#handleSettle();
  };

  #onScrollFallback = (): void => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#handleSettle, SETTLE_TIMEOUT);
  };

  #handleSettle = (): void => {
    if (this.#repositioning) return;
    if (this.#dragPaused) return;
    if (this.refs.track.dataset.dragging !== undefined) return;

    const track = this.refs.track;
    const scrollLeft = track.scrollLeft;
    const children = this.#trackChildren();
    if (children.length === 0) return;

    let closest = children[0];
    let closestDist = Math.abs(closest.offsetLeft - scrollLeft);
    for (let i = 1; i < children.length; i++) {
      const dist = Math.abs(children[i].offsetLeft - scrollLeft);
      if (dist < closestDist) {
        closest = children[i];
        closestDist = dist;
      }
    }

    if (!("clone" in closest.dataset)) return;

    const realIndex = closest.dataset.index;
    const realSlide = children.find(
      (el) => !("clone" in el.dataset) && el.dataset.index === realIndex,
    );
    if (realSlide) {
      this.#reposition(realSlide);
    }
  };

  #trackChildren(): HTMLElement[] {
    return Array.from(this.refs.track.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement,
    );
  }

  #reposition(realSlide: HTMLElement): void {
    this.#repositioning = true;
    const track = this.refs.track;
    track.dataset.repositioning = "";
    track.scrollLeft = realSlide.offsetLeft;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        delete track.dataset.repositioning;
        this.#repositioning = false;
      });
    });
  }

  #scrollByItems(count: number): void {
    const track = this.refs.track;
    const scrollLeft = track.scrollLeft;
    const children = this.#trackChildren();
    if (children.length === 0) return;

    let currentIdx = 0;
    let currentDist = Math.abs(children[0].offsetLeft - scrollLeft);
    for (let i = 1; i < children.length; i++) {
      const dist = Math.abs(children[i].offsetLeft - scrollLeft);
      if (dist < currentDist) {
        currentIdx = i;
        currentDist = dist;
      }
    }

    const targetIdx = Math.max(0, Math.min(children.length - 1, currentIdx + count));
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({
      left: children[targetIdx].offsetLeft,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  }

  #onMediaChange = (): void => {
    const track = this.refs.track;
    const scrollLeft = track.scrollLeft;
    const children = this.#trackChildren();
    if (children.length === 0) return;

    let closest = children[0];
    let closestDist = Math.abs(closest.offsetLeft - scrollLeft);
    for (let i = 1; i < children.length; i++) {
      const dist = Math.abs(children[i].offsetLeft - scrollLeft);
      if (dist < closestDist) {
        closest = children[i];
        closestDist = dist;
      }
    }

    track.scrollLeft = closest.offsetLeft;
  };

  #onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      this.#scrollByItems(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      this.#scrollByItems(1);
    }
  };

  #setupAutoplay(): void {
    this.#autoplayEnabled = this.dataset.autoplay !== undefined;
    if (!this.#autoplayEnabled) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const parsed = parseInt(this.dataset.autoplayInterval ?? "", 10);
    this.#autoplayIntervalMs = Number.isFinite(parsed) && parsed > 0 ? parsed : 5000;

    this.#inViewport = false;
    this.#documentVisible = !document.hidden;

    const track = this.refs.track;
    track.addEventListener("pointerenter", this.#onPointerEnter, { signal: this.signal });
    track.addEventListener("pointerleave", this.#onPointerLeave, { signal: this.signal });
    this.addEventListener("focusin", this.#onFocusIn, { signal: this.signal });
    this.addEventListener("focusout", this.#onFocusOut, { signal: this.signal });

    document.addEventListener("visibilitychange", this.#onVisibilityChange, {
      signal: this.signal,
    });

    this.#intersectionObserver = new IntersectionObserver(this.#onIntersect, { threshold: 0.25 });
    this.#intersectionObserver.observe(this);

    this.#scheduleNextTick();
  }

  #scheduleNextTick = (): void => {
    if (!this.#autoplayEnabled) return;
    clearTimeout(this.#autoplayTimer);
    this.#autoplayTimer = setTimeout(this.#tick, this.#autoplayIntervalMs);
  };

  #tick = (): void => {
    if (this.#canAutoplay()) {
      this.#scrollByItems(1);
    }
    this.#scheduleNextTick();
  };

  #canAutoplay = (): boolean =>
    !this.#hoverPaused &&
    !this.#dragPaused &&
    !this.#focusPaused &&
    this.#inViewport &&
    this.#documentVisible &&
    !this.#repositioning;

  #onPointerEnter = (): void => {
    this.#hoverPaused = true;
    clearTimeout(this.#autoplayTimer);
  };

  #onPointerLeave = (): void => {
    this.#hoverPaused = false;
    this.#scheduleNextTick();
  };

  #onPointerDown = (): void => {
    this.#dragPaused = true;
    clearTimeout(this.#autoplayTimer);
  };

  #onPointerUp = (): void => {
    const track = this.refs.track;
    const controller = new AbortController();
    const clear = () => {
      controller.abort();
      clearTimeout(this.#pointerUpTimer);
      this.#pointerUpTimer = undefined;
      this.#dragPaused = false;
      this.#handleSettle();
      this.#scheduleNextTick();
    };
    if ("onscrollend" in window) {
      track.addEventListener("scrollend", clear, {
        once: true,
        signal: AbortSignal.any([this.signal, controller.signal]),
      });
    }
    clearTimeout(this.#pointerUpTimer);
    this.#pointerUpTimer = setTimeout(clear, POINTER_UP_SETTLE_TIMEOUT);
  };

  #onFocusIn = (): void => {
    this.#focusPaused = true;
    clearTimeout(this.#autoplayTimer);
  };

  #onFocusOut = (e: FocusEvent): void => {
    if (e.relatedTarget instanceof Node && this.contains(e.relatedTarget)) return;
    this.#focusPaused = false;
    this.#scheduleNextTick();
  };

  #onVisibilityChange = (): void => {
    this.#documentVisible = !document.hidden;
    if (this.#documentVisible) this.#scheduleNextTick();
    else clearTimeout(this.#autoplayTimer);
  };

  #onIntersect = (entries: IntersectionObserverEntry[]): void => {
    const entry = entries[0];
    if (!entry) return;
    this.#inViewport = entry.isIntersecting;
    if (entry.isIntersecting) this.#scheduleNextTick();
    else clearTimeout(this.#autoplayTimer);
  };

  disconnectedCallback(): void {
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#autoplayTimer);
    clearTimeout(this.#pointerUpTimer);
    this.#intersectionObserver?.disconnect();
    super.disconnectedCallback();
  }
}

if (!customElements.get("related-products")) {
  customElements.define("related-products", RelatedProducts);
}
